import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Pencil, Link2, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../api";
import { renderField, ReadonlyField, buildCreatePayload, buildEditDiff } from "./adminRecord/fieldRenderer";

function RelatedLinks({ related, schema, pk }) {
  if (!related?.length) return null;
  return (
    <div className="mb-5 p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid="admin-related-panel">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#978D78] font-bold mb-2 flex items-center gap-1.5">
        <Link2 size={12} /> Registos relacionados
      </p>
      <div className="flex flex-wrap gap-2">
        {related.map((rel) => (
          <Link
            key={`rel-${rel.table}-${rel.column}`}
            to={`/admin/t/${rel.schema}/${rel.name}?fcol=${encodeURIComponent(rel.column)}&fval=${encodeURIComponent(pk)}`}
            data-testid={`admin-related-${rel.schema}-${rel.name}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-[#E6E1D6] bg-[#FAF8F2] hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition"
          >
            {rel.label}
            <span className="text-[9px] font-mono opacity-60">.{rel.column}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// Diagnóstico/fallback para casos em que o cliente não recebe o email de
// recuperação (filtros SPAM corporativos, etc.). Gera o token de reset
// directamente no servidor e mostra o URL para o admin copiar e enviar
// manualmente (WhatsApp/SMS). Só disponível em cliente.Tinf_Cliente.
function PasswordResetLinkPanel({ clientId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    setLoading(true); setError("");
    try {
      const r = await adminApi.generateResetLink(clientId);
      setData(r);
      setCopied(false);
    } catch (e) {
      setError(e.message || "Erro ao gerar link");
    } finally { setLoading(false); }
  };

  const copy = async () => {
    if (!data?.reset_url) return;
    try {
      await navigator.clipboard.writeText(data.reset_url);
      setCopied(true);
      toast.success("Link copiado para a área de transferência");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Não foi possível copiar — selecione o texto manualmente");
    }
  };

  return (
    <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-md" data-testid="admin-reset-link-panel">
      <p className="text-[10px] uppercase tracking-[0.18em] text-amber-900 font-bold mb-2 flex items-center gap-1.5">
        <KeyRound size={12} /> Recuperação de palavra-passe (manual)
      </p>
      <p className="text-[11px] text-amber-900/80 mb-3 leading-relaxed">
        Gera um link de reposição com validade de 30 minutos. Use quando o cliente
        não recebe o email automático (ex.: filtros SPAM corporativos). Copie o
        URL e envie manualmente via WhatsApp/SMS.
      </p>
      {!data && (
        <button
          type="button" onClick={generate} disabled={loading}
          data-testid="admin-generate-reset-link"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 transition"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
          {loading ? "A gerar…" : "Gerar link de reposição"}
        </button>
      )}
      {error && (
        <div className="mt-2 text-[11px] text-red-700" data-testid="admin-reset-link-error">{error}</div>
      )}
      {data && (
        <div className="space-y-2" data-testid="admin-reset-link-output">
          <div className="flex items-center gap-2">
            <input
              type="text" value={data.reset_url} readOnly
              data-testid="admin-reset-link-url"
              onFocus={(e) => e.target.select()}
              className="flex-1 px-2 py-1.5 text-[11px] font-mono bg-white border border-amber-300 rounded text-[#0F1829]"
            />
            <button
              type="button" onClick={copy}
              data-testid="admin-reset-link-copy"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] rounded bg-[#0F1829] text-white hover:bg-[#001f6e] transition"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copiado" : "Copiar"}
            </button>
          </div>
          <div className="text-[10px] text-amber-900/70">
            Expira em {data.expires_in_minutes} min ·{" "}
            <button type="button" onClick={generate}
              data-testid="admin-reset-link-regen"
              className="underline hover:text-amber-900">
              Gerar novo link
            </button>
          </div>
        </div>
      )}
      <hr className="my-3 border-amber-200" />
      <ManualSetPassword clientId={clientId} />
    </div>
  );
}

// Manual password override — for clients without email access (elderly,
// phone support, etc.). Admin types a temporary password and the client
// can log in immediately.
function ManualSetPassword({ clientId }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const submit = async () => {
    if (pw.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.setClientPassword(clientId, pw);
      toast.success("Palavra-passe definida. Comunique-a ao cliente.");
      setDone(true);
    } catch (e) {
      toast.error(e.message || "Erro ao definir palavra-passe");
    } finally { setSaving(false); setConfirming(false); }
  };

  if (!open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)}
        data-testid="admin-manual-pw-open"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 transition"
      >
        <KeyRound size={13} /> Definir palavra-passe manualmente
      </button>
    );
  }
  if (done) {
    return (
      <div className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="admin-manual-pw-done">
        <Check size={13} /> Palavra-passe definida com sucesso. Comunique ao cliente.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="admin-manual-pw-form" translate="no">
      <p className="text-[11px] text-amber-900/80">
        Use apenas em suporte telefónico quando o cliente não tem acesso a email.
        Mínimo 6 caracteres. A acção é registada na auditoria. A password fica visível
        para a poder comunicar ao cliente.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="text" value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="admin-manual-pw-input"
          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
          placeholder="Nova palavra-passe"
          className="flex-1 px-2 py-1.5 text-[12px] font-mono bg-white border border-amber-300 rounded text-[#0F1829]"
        />
        {confirming ? (
          <>
            <button type="button" onClick={submit} disabled={saving}
              data-testid="admin-manual-pw-confirm"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 transition">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {saving ? "A guardar…" : "Confirmar"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} disabled={saving}
              data-testid="admin-manual-pw-confirm-cancel"
              className="text-[11px] text-amber-900/70 hover:text-amber-900 underline">
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setConfirming(true)} disabled={pw.length < 6}
              data-testid="admin-manual-pw-submit"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 transition">
              <Save size={13} /> Definir
            </button>
            <button type="button" onClick={() => { setOpen(false); setPw(""); }}
              data-testid="admin-manual-pw-cancel"
              className="text-[11px] text-amber-900/70 hover:text-amber-900 underline">
              Cancelar
            </button>
          </>
        )}
      </div>
      {confirming && (
        <p className="text-[11px] text-red-700 font-medium" data-testid="admin-manual-pw-confirm-warning">
          Vai definir a palavra-passe deste cliente para <span className="font-mono">&quot;{pw}&quot;</span>. Tem a certeza?
        </p>
      )}
    </div>
  );
}

export default function AdminRecord({ viewOnly = false }) {
  const { schema, name, pk } = useParams();
  const nav = useNavigate();
  const isCreate = !pk || pk === "new";
  const isView = viewOnly && !isCreate;
  const [meta, setMeta] = useState(null);
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const errRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const m = await adminApi.tableSchema(schema, name);
      setMeta(m);
      if (!isCreate) {
        const r = await adminApi.tableGet(schema, name, pk);
        setValues(r.data || {});
        setInitialValues(r.data || {});
      } else {
        setValues({});
        setInitialValues({});
      }
      // Related records (only when we have a concrete record open)
      if (!isCreate) {
        try {
          const r = await adminApi.tableRelated(schema, name);
          setRelated(r.data || []);
        } catch (e) { console.error("related", e); setRelated([]); }
      }
    } catch (e) {
      setError(e.message || "Erro");
    } finally { setLoading(false); }
  }, [schema, name, pk, isCreate]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const payload = buildCreatePayload(values, meta?.columns || []);
    const r = await adminApi.tableCreate(schema, name, payload);
    setOkMsg(`Criado com ID ${r.pk}`);
    setTimeout(() => nav(`/admin/t/${schema}/${name}/${encodeURIComponent(r.pk)}`, { replace: true }), 600);
  };

  const handleEdit = async () => {
    const diff = buildEditDiff(values, initialValues, meta?.columns || []);
    if (Object.keys(diff).length === 0) {
      setOkMsg("Nada para guardar.");
      setTimeout(() => setOkMsg(""), 2000);
      return;
    }
    await adminApi.tableUpdate(schema, name, pk, diff);
    setOkMsg(`Guardado (${Object.keys(diff).length} ${Object.keys(diff).length === 1 ? "campo" : "campos"}).`);
    setInitialValues({ ...initialValues, ...diff });
    setTimeout(() => setOkMsg(""), 2500);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setOkMsg("");
    try {
      // Client-side validation: check NOT NULL fields on create
      if (isCreate) {
        const required = (meta.columns || []).filter(c =>
          c.IS_NULLABLE === "NO" &&
          !c.IsIdentity &&
          c.COLUMN_DEFAULT == null,
        );
        const missing = required.filter(c => {
          const v = values[c.COLUMN_NAME];
          return v === undefined || v === null || v === "";
        });
        if (missing.length > 0) {
          const msg = `Faltam preencher ${missing.length} ${missing.length === 1 ? "campo obrigatório" : "campos obrigatórios"}: ${missing.map(c => c.COLUMN_NAME).join(", ")}`;
          setError(msg);
          toast.error(msg);
          if (errRef.current) errRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          setSaving(false);
          return;
        }
      }
      if (isCreate) await handleCreate();
      else await handleEdit();
    } catch (e2) {
      const msg = e2.message || "Erro ao guardar";
      setError(msg);
      toast.error(msg, { description: `HTTP ${e2.status || "?"} · ${schema}.${name}${isCreate ? "" : ` #${pk}`}` });
      if (errRef.current) errRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-[#4B5563]"><Loader2 size={18} className="animate-spin inline mr-1" /> A carregar...</div>;
  if (!meta) return <div className="text-sm text-red-600">{error || "Erro"}</div>;

  const canEdit = (meta.ops || []).includes("edit");

  let title;
  if (isCreate) title = `Novo ${meta.label}`;
  else if (isView) title = `${meta.label} #${pk}`;
  else title = `Editar ${meta.label} #${pk}`;

  return (
    <div data-testid="admin-record-page">
      <button onClick={() => nav(`/admin/t/${schema}/${name}`)} data-testid="admin-back-btn"
        className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar à lista
      </button>
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{title}</h1>
          <p className="text-xs font-mono text-[#978D78]">{schema}.{name}</p>
        </div>
        {isView && canEdit && (
          <button onClick={() => nav(`/admin/t/${schema}/${name}/${encodeURIComponent(pk)}`)}
            data-testid="admin-switch-to-edit"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0F1829] text-white text-sm rounded hover:bg-[#001f6e] transition">
            <Pencil size={14} /> Editar
          </button>
        )}
      </div>

      {!isCreate && <RelatedLinks related={related} schema={schema} pk={pk} />}

      {!isCreate && schema === "cliente" && name === "Tinf_Cliente" && (
        <PasswordResetLinkPanel clientId={pk} />
      )}

      {error && (
        <div ref={errRef} data-testid="admin-save-error"
          className="mb-3 px-4 py-3 bg-red-50 border border-red-300 text-sm text-red-800 rounded shadow-sm">
          <div className="font-bold uppercase tracking-[0.1em] text-[11px] text-red-600 mb-1">Erro ao guardar</div>
          <div className="whitespace-pre-wrap break-words">{error}</div>
        </div>
      )}
      {okMsg && <div data-testid="admin-save-ok" className="mb-3 px-3 py-2 bg-green-50 border border-green-200 text-sm text-green-800 rounded">{okMsg}</div>}

      <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-5 border border-[#E6E1D6] rounded-md" data-testid="admin-record-form">
        {(meta.columns || []).map((c) => {
          const isPk = c.COLUMN_NAME === meta.pk;
          const disabled = isView || (isCreate ? c.IsIdentity : isPk);
          const colSpan = c.DATA_TYPE === "text" || c.DATA_TYPE === "ntext" ? "md:col-span-2" : "";
          return (
            <div key={c.COLUMN_NAME} className={colSpan}>
              <label className="text-[11px] uppercase tracking-[0.15em] text-[#978D78] flex items-center gap-1.5">
                {c.COLUMN_NAME}
                {c.IS_NULLABLE === "NO" && !c.IsIdentity && (
                  <span title="Campo obrigatório" className="text-red-500 text-sm leading-none -mt-0.5">*</span>
                )}
                <span className="text-[9px] font-mono normal-case tracking-normal text-[#6B7280]">
                  {c.DATA_TYPE}{c.CHARACTER_MAXIMUM_LENGTH > 0 ? `(${c.CHARACTER_MAXIMUM_LENGTH})` : ""}
                </span>
              </label>
              <div className="mt-1">
                {disabled
                  ? <ReadonlyField col={c} value={values[c.COLUMN_NAME]} schema={schema} name={name} isCreate={isCreate} />
                  : renderField(c, values[c.COLUMN_NAME],
                      (v) => setValues((prev) => ({ ...prev, [c.COLUMN_NAME]: v })),
                      c.FK ? { schema, name } : null)}
              </div>
            </div>
          );
        })}
        {!isView && (
          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-2 border-t border-[#E6E1D6] mt-2">
            <button type="button" onClick={() => nav(`/admin/t/${schema}/${name}`)}
              data-testid="admin-cancel-btn"
              className="px-4 py-2 text-sm rounded border border-[#E6E1D6] hover:bg-[#F5F2EB]">Cancelar</button>
            <button type="submit" disabled={saving}
              data-testid="admin-save-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-[#0F1829] text-white rounded hover:bg-[#001f6e] disabled:opacity-40">
              <Save size={14} /> {saving ? "A guardar..." : (isCreate ? "Criar" : "Guardar")}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
